import classnames from 'classnames/bind'
import { AnimatePresence, m } from 'framer-motion'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalInputPreset } from '~/@types/input-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { POPIN_TAGS } from '~/lib/shopify-tags'

import { useInputPreset } from '~/components/Abstracts/Form/Input/hooks'
import { Sizes } from '~/components/Abstracts/Image'
import LoadingComponent from '~/components/Abstracts/LoadingComponent'
import RichText from '~/components/Abstracts/RichText'
import Checkbox from '~/components/Form/Checkbox'
import Input from '~/components/Form/Input'
import Image from '~/components/UI/Image'
import SquaredCta from '~/components/UI/SquaredCta'

import RecaptchaHandler from '~/providers/RecaptchaProvider/RecaptchaHandler'
import { useStyle } from '~/providers/StyleProvider'

import useShopifyNewsletterMutation from '~/hooks/account/useShopifyNewsletterMutation'
import useFormWithMutation from '~/hooks/useFormWithMutation'

import { FORM } from '~/data/dictionary'
import { serializePopinContests } from '~/data/global-data/serialize-popin-contests'

import css from './style.module.scss'

const PopIn = dynamic(() => import('~/components/UI/Popin'), {
  loading: () => <LoadingComponent />,
})

const cx = classnames.bind(css)

export type PopinContestProps = ReturnType<typeof serializePopinContests> & {
  onPopinClose?: () => void
  UITheme?: 'dark' | 'light'
}

const IMAGE_SIZES: Sizes = [{ ratio: 1 }, { breakpoint: 'l', ratio: 22 / 24 }]

function PopinContests({
  UITheme = 'dark',
  backgroundImage,
  logo,
  paragraph,
  submitLabel,
  rulesLabel,
  shouldHidePopinContests,
  onPopinClose,
  checkboxes,
  mobileBackgroundImage,
  confirmationMessage,
}: PopinContestProps) {
  const t = useTranslate()

  const inputEmailPreset = useInputPreset(GlobalInputPreset.Email, {
    placeholder: t(FORM.NEWSLETTER_EMAIL),
  })

  const inputFirstNamePreset = useInputPreset(GlobalInputPreset.FirstName, {
    placeholder: t(FORM.FIRST_NAME),
  })

  const inputLastNamePreset = useInputPreset(GlobalInputPreset.LastName, {
    placeholder: t(FORM.LAST_NAME),
  })

  const [result, setResult] = useState<boolean | string>(false)

  const paragraphStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Haffer,
    color:
      UITheme === 'dark' ? GlobalThemeColors.Black : GlobalThemeColors.White,
  })

  const labelStyle = useStyle({
    textPreset: GlobalTextPreset.Label11Haffer,
    color:
      UITheme === 'dark' ? GlobalThemeColors.DoveGray : GlobalThemeColors.Alto,
  })

  const { mutate: newsletterMutation, isLoading } =
    useShopifyNewsletterMutation(
      {
        onSuccess: () => {
          setResult(confirmationMessage)
        },
      },
      (...params) => form.setError(...params),
    )

  const { form, onSubmit } = useFormWithMutation(
    newsletterMutation,
    null,
    'newsletter',
  )

  useEffect(() => {
    if (
      form?.formState?.errors &&
      Object.keys(form.formState.errors).length > 0
    )
      setResult(false)
  }, [form.formState])

  if (shouldHidePopinContests || !backgroundImage || !paragraph) return null

  return (
    <AnimatePresence>
      <m.div
        className={css.PopinContests}
        initial={{ translateY: 15, opacity: 0 }}
        animate={{
          translateY: 0,
          opacity: 1,
          transition: {
            duration: 0.5,
            ease: [0.345, 0.45, 0.39, 0.94],
          },
        }}
        exit={{
          opacity: 0,
          transition: {
            duration: 0.5,
          },
        }}>
        <PopIn
          className={css.popinWrapper}
          closeClassName={cx(css.closeButton)}
          closeIconTheme={UITheme}
          onClose={onPopinClose}>
          <Image
            {...backgroundImage}
            imageClassName={css.backgroundImage}
            className={cx(css.backgroundImageWrapper, css.desktopImage)}
            sizesFromBreakpoints={IMAGE_SIZES}
          />
          <Image
            {...mobileBackgroundImage}
            className={cx(css.backgroundImageWrapper, css.mobileImage)}
            sizesFromBreakpoints={IMAGE_SIZES}
          />
          <div className={cx(css.formContainer, { hasNoLogo: !logo })}>
            {logo && <Image className={css.logo} {...logo} />}
            <RecaptchaHandler className={css.recaptchaHandler}>
              <FormProvider {...form}>
                <form className={css.form} onSubmit={onSubmit}>
                  <RichText
                    className={cx(css.paragraph, paragraphStyle)}
                    render={paragraph}
                  />
                  <div className={cx(css.inputsContainer)}>
                    <div className={cx(css.firstNameAndLastName, css.inputs)}>
                      <Input
                        inputClassName={css.firstName}
                        color={GlobalThemeColors.DoveGray}
                        className={cx(css.nameInput)}
                        {...inputFirstNamePreset}
                      />
                      <Input
                        inputClassName={css.lastName}
                        color={GlobalThemeColors.DoveGray}
                        className={cx(css.nameInput)}
                        {...inputLastNamePreset}
                      />
                    </div>
                    <Input
                      inputClassName={cx(css.inputs, css.email)}
                      color={GlobalThemeColors.DoveGray}
                      {...inputEmailPreset}
                    />
                  </div>
                  {/* PUIG data team wants the from to be the campaign name */}
                  <Input
                    type="hidden"
                    name="from"
                    className={css.hiddenFromInput}
                    value={POPIN_TAGS.NINA_CONTEST}
                  />
                  {checkboxes && (
                    <div className={css.checkboxesContainer}>
                      {checkboxes?.map((label, index) => (
                        <Checkbox
                          key={index}
                          id={`gdpr-popin-contests-${index}`}
                          name={`gdpr-popin-contests-${index}`}
                          required={true}
                          verticalAlign="baseline"
                          theme={
                            UITheme === 'dark'
                              ? GlobalThemeColors.DoveGray
                              : GlobalThemeColors.Alto
                          }
                          className={cx(css.checkbox, labelStyle)}
                          label={label ?? t(FORM.ACCEPT_TERMS)}></Checkbox>
                      ))}
                    </div>
                  )}

                  {result && <div className={cx(labelStyle)}>{result}</div>}

                  <SquaredCta
                    disabled={isLoading}
                    className={css.submitBtn}
                    theme={
                      UITheme === 'dark'
                        ? GlobalThemeColors.Black
                        : GlobalThemeColors.White
                    }
                    type={'submit'}>
                    {isLoading ? (
                      <LoadingComponent color={'white'} />
                    ) : (
                      submitLabel
                    )}
                  </SquaredCta>
                </form>
              </FormProvider>
            </RecaptchaHandler>
          </div>
          <p className={cx(labelStyle, css.rulesLabel)}>{rulesLabel}</p>
        </PopIn>
      </m.div>
    </AnimatePresence>
  )
}

export default PopinContests
